import { createSelector } from 'reselect';
import { DateTime } from "luxon";
import { Map as ImmutableMap } from 'immutable';
import { getTransactionsById } from 'data/transactions/selectors';
import { dateTimeFromTxn } from 'data/transactions/utils';
import { getScheduledTransactions } from 'data/scheduledTransactions/scheduledTransactionsSelectors';
import { CoaTypeEnum } from 'data/chartOfAccounts/types';


const txnIsPosted = (txn) => txn.source !== 'SCHEDULED_TRANSACTION_PENDING';

const transferCheck = (txn, model) => {
  if (txn.coa) {
    if (txn.coa.type === CoaTypeEnum.ACCOUNT) {
      // make sure only one side of the transfer displays based on matching the model
      return (model && Math.sign(model.transaction?.amount) === Math.sign(txn.amount))
    }
    return true
  }
  return false;
};

export const getPostedAndExpectedScheduledInstances = createSelector(
  getTransactionsById,
  getScheduledTransactions,
  (state, daysBack) => daysBack,
  (transactions, scheduledTxns, daysBack) => {
    let posted = new Map();
    let expected = new Map();
    const today = DateTime.local().startOf('day');
    const pastCutoff = today.minus({ days: daysBack });
    const futureCutoff = today.plus({ weeks: 4 });
    transactions.forEach((txn) => {
      const txnDate = txn.postedOn && DateTime.fromISO(txn.postedOn) || txn.stDueOn && DateTime.fromISO(txn.stDueOn);
      if (txn.isBill || txn.isSubscription || txn.stModelId) {
        const model = scheduledTxns.get(txn.stModelId);
        if (txnIsPosted(txn)) {
          if (txnDate && (txnDate > pastCutoff) && transferCheck(txn, model)) {
            posted.set(txn.id, txn);
          }
        } else if (txnDate && (txnDate < futureCutoff) && transferCheck(txn, model)) { // expected
          expected.set(txn.id, txn);
        }
      }
      // Adding future dated manual transactions to the upcoming page
      if (txn.source === 'USER_ENTERED' && txnDate && (txnDate < futureCutoff && txnDate > today)) {
        expected.set(txn.id, txn);
      }
    });
    posted = ImmutableMap(posted.entries()).sort((a, b) => new Date(b.postedOn) - new Date(a.postedOn));
    expected = ImmutableMap(expected.entries()).sort((a, b) => new Date(a.stDueOn || a.postedOn) - new Date(b.stDueOn || b.postedOn));
    return ({
      posted,
      expected,
    });
  }
);


export const getModelHistory = createSelector(
  getTransactionsById,
  (state, model) => model,
  (transactions, model) => {
    const monthCutoff = DateTime.local().endOf('month');

    let oldestPending = { postedOn: '6942-01-01' };
    let pastModelTxns = new ImmutableMap();
    if (model) {
      oldestPending = { ...model.transaction, postedOn: '6942-01-01' };
      pastModelTxns = transactions ? transactions.filter((txn) => {
        if (txn.stModelId === model.id && transferCheck(txn, model)) {
          const txnDate = dateTimeFromTxn(txn);
          if (txn.source === 'SCHEDULED_TRANSACTION_PENDING' && txnDate < dateTimeFromTxn(oldestPending)) {
            oldestPending = txn;
          }
          return txnDate < monthCutoff;
        }
        return false;
      }) : ImmutableMap();
    }
    return { history: pastModelTxns.sort((a, b) => new Date(b.postedOn) - new Date(a.postedOn)), txn: oldestPending };
  }
);

export const generateChartData = createSelector(
  (history) => history,
  (history, negator) => negator,
  (history, negator) => {
    if (!history) return {chartData: [], average: 0};

    const pastCutoff = DateTime.local().startOf('month').minus({ months: 5 });
    const periods = [];
    for (let i = 0; i < 6; i++) {
      const currentMonth = pastCutoff.plus({months: i});
      periods.push({  // eslint-disable-line
        month: currentMonth.toFormat('MMM').toUpperCase(),
        totalAmount: 0,
        currentAmount: 0,
      });
    }

    history.forEach((txn) => {
      const txnDate = dateTimeFromTxn(txn);
      if (txnDate && (txnDate > pastCutoff)) {
        const periodIndex = periods.findIndex((period) => period.month === txnDate.toFormat('MMM').toUpperCase());
        const amt = Number(txn.amount);
        if (periodIndex > -1) {
          periods[periodIndex].totalAmount += amt;
          if (txnIsPosted(txn)) { // posted
            periods[periodIndex].currentAmount += amt;
          }
        }
      }
    });

    let sum = 0;
    let activeMonths = 0;
    periods.forEach((period, i) => {
      if (period.totalAmount) activeMonths += 1;
      const newAmount = period.totalAmount * negator;
      sum += newAmount;
      periods[i].totalAmount = newAmount;
      periods[i].currentAmount *= negator;
    });
    if (activeMonths < 1) activeMonths = 1;
    const average = Math.round(sum / activeMonths);

    return { chartData: periods, average, sum };
  }
);

